import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/TextField'
import { AuthLayout } from '@/features/auth/AuthLayout'
import { instanceQueryOptions, meQueryOptions, useLogin } from '@/features/auth/api'
import { errorMessage } from '@/lib/graphql'

interface LoginSearch {
  /** Where to go once signed in, set by the `_app` guard. */
  redirect?: string
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: async ({ context: { queryClient } }) => {
    // Already signed in? The sign-in page has nothing to offer.
    const { me } = await queryClient.ensureQueryData(meQueryOptions())
    if (me) throw redirect({ to: '/' })
  },
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(instanceQueryOptions()),
  component: LoginPage,
})

function LoginPage() {
  const { redirect: redirectTo } = Route.useSearch()
  const { instance } = Route.useLoaderData()
  const navigate = useNavigate()
  const login = useLogin()

  const [handle, setHandle] = useState('')
  const [password, setPassword] = useState('')

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    login.mutate(
      { handle, password },
      {
        onSuccess: () => {
          // `href` from the guard is a full path, so it goes through `to` as-is.
          void navigate({ to: redirectTo ?? '/' })
        },
      },
    )
  }

  // A brand new instance has nobody to sign in as. Point at registration
  // instead of at a form that cannot work yet.
  if (instance.needsSetup) {
    return (
      <AuthLayout
        title="Nobody lives here yet"
        subtitle="This instance is brand new. The first account to register becomes its admin."
      >
        <Link to="/register" className="btn btn-primary btn-block">
          Claim this instance
        </Link>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your lounges.">
      <form onSubmit={submit} className="auth-form">
        <TextField
          label="Handle"
          value={handle}
          onChange={(event) => setHandle(event.target.value)}
          autoComplete="username"
          autoFocus
          required
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />

        {login.isError && (
          <p className="form-error" role="alert">
            {errorMessage(login.error, 'That did not work. Try again.')}
          </p>
        )}

        <Button type="submit" variant="primary" block disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      {instance.registrationPolicy === 'OPEN' && (
        <p className="muted auth-aside">
          No account yet? <Link to="/register">Register</Link>
        </p>
      )}
      {instance.registrationPolicy === 'INVITE_ONLY' && (
        <p className="muted auth-aside">This instance is invite-only. Ask a member for a link.</p>
      )}
      {instance.registrationPolicy === 'CLOSED' && (
        <p className="muted auth-aside">This instance is not accepting new accounts.</p>
      )}
    </AuthLayout>
  )
}
